import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Key, CheckCircle2, XCircle, ExternalLink, X, Zap } from 'lucide-react';
import { getOnboardingStatus } from '../api';
import { isAuthenticated } from '../auth';

const STEPS = [
  {
    id: 'gemini',
    label: 'Clé Google Gemini',
    description: 'Requise pour l\'analyse IA du contenu SEO, les suggestions et l\'audit complet',
    configuredKey: 'gemini_configured',
    required: true,
    badge: { label: 'Requis', className: 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-200/80 shadow-sm' },
    link: 'https://aistudio.google.com/app/apikey',
    linkLabel: 'Obtenir la clé sur Google AI Studio',
    steps: [
      'Rendez-vous sur Google AI Studio',
      'Connectez-vous avec votre compte Google',
      'Cliquez sur « Create API Key »',
      'Copiez la clé et collez-la dans Profil → Clés API',
    ],
  },
  {
    id: 'pagespeed',
    label: 'Clé Google PageSpeed',
    description: 'Recommandée pour l\'analyse de performance et Core Web Vitals',
    configuredKey: 'pagespeed_configured',
    required: false,
    recommended: true,
    badge: { label: 'Recommandé', className: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200/80 shadow-sm' },
    link: 'https://console.cloud.google.com/apis/credentials',
    linkLabel: 'Obtenir la clé sur Google Cloud Console',
    steps: [
      'Allez sur Google Cloud Console',
      'Créez ou sélectionnez un projet',
      'Activez l\'API « PageSpeed Insights API »',
      'Créez une clé API dans « Identifiants »',
    ],
  },
  {
    id: 'semrush',
    label: 'Clé Semrush',
    description: 'Optionnelle — trafic organique, mots-clés et score d\'autorité',
    configuredKey: 'semrush_configured',
    required: false,
    badge: { label: 'Optionnel', className: 'bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-200/80 shadow-sm' },
    link: 'https://www.semrush.com/',
    linkLabel: 'Obtenir la clé sur Semrush',
    steps: [
      'Connectez-vous sur semrush.com',
      'Allez dans Account → Profile → API',
      'Copiez votre clé API',
    ],
  },
];

export default function OnboardingBanner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem('onboarding_dismissed') === 'true';
  });

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      const data = await getOnboardingStatus();
      setStatus(data);
    } catch {
      // silent fail — ne pas bloquer l'app
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Rafraîchir quand des clés sont sauvegardées depuis Profile
  useEffect(() => {
    const handler = () => {
      setDismissed(false);
      sessionStorage.removeItem('onboarding_dismissed');
      fetchStatus();
    };
    window.addEventListener('api-keys-updated', handler);
    return () => window.removeEventListener('api-keys-updated', handler);
  }, [fetchStatus]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('onboarding_dismissed', 'true');
  };

  const goToApiKeys = () => {
    navigate('/profile', { state: { tab: 'apikeys' } });
  };

  if (loading || !status || status.setup_complete || dismissed) return null;

  const missingRequired = STEPS.filter(s => s.required && !status[s.configuredKey]).length;
  const configuredCount = STEPS.filter(s => status[s.configuredKey]).length;

  return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden shadow-sm">
      {/* En-tête du banner */}
      <div className="px-4 py-3 flex items-center gap-3">
        <div className="flex-shrink-0 h-9 w-9 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center">
          <Key size={16} className="text-amber-600" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-amber-900">
              {missingRequired > 0
                ? `${missingRequired} clé${missingRequired > 1 ? 's' : ''} API requise${missingRequired > 1 ? 's' : ''} manquante${missingRequired > 1 ? 's' : ''}`
                : 'Clés optionnelles non configurées'}
            </p>
            <span className="text-xs text-amber-600 bg-amber-100 rounded-full px-2 py-0.5 font-medium">
              {configuredCount}/{STEPS.length} configurées
            </span>
          </div>
          <p className="text-xs text-amber-700 mt-0.5 hidden sm:block">
            Configurez vos clés API pour accéder à toutes les fonctionnalités d'analyse SEO
          </p>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={goToApiKeys}
            className="text-xs font-semibold bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <Zap size={12} />
            Configurer
          </button>
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-100 rounded-lg transition-colors"
            aria-label={expanded ? 'Réduire le guide' : 'Voir le guide de configuration'}
            title={expanded ? 'Réduire' : 'Voir le guide'}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            onClick={handleDismiss}
            className="p-1.5 text-amber-400 hover:text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
            aria-label="Masquer ce guide pour cette session"
            title="Masquer"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Guide détaillé (expandable) */}
      {expanded && (
        <div className="border-t border-amber-200 bg-white/70 px-4 pt-4 pb-5">
          <p className="text-xs font-medium text-amber-800 mb-3 flex items-center gap-1.5">
            <Key size={12} />
            Guide de configuration — suivez ces étapes pour débloquer toutes les fonctionnalités
          </p>

          <div className="space-y-2.5">
            {STEPS.map((step) => {
              const configured = status[step.configuredKey];
              return (
                <div
                  key={step.id}
                  className={`rounded-lg border transition-colors ${
                    configured
                      ? 'border-green-200 bg-green-50/70'
                      : step.required
                      ? 'border-red-200 bg-red-50/50'
                      : 'border-blue-gray-100 bg-white/80'
                  }`}
                >
                  <div className="flex items-start gap-3 p-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {configured ? (
                        <CheckCircle2 size={18} className="text-green-500" />
                      ) : (
                        <XCircle size={18} className={step.required ? 'text-red-400' : 'text-blue-gray-300'} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className={`text-sm font-semibold ${configured ? 'text-green-800' : 'text-blue-gray-800'}`}>
                          {step.label}
                        </span>
                        <span
                          className={`inline-flex items-center text-[11px] font-semibold leading-none rounded-full px-2.5 py-1.5 ${step.badge.className}`}
                        >
                          {step.badge.label}
                        </span>
                        {configured && (
                          <span className="text-[10px] text-green-600 font-medium">✓ Configuré</span>
                        )}
                      </div>
                      <p className="text-xs text-blue-gray-500">{step.description}</p>

                      {!configured && (
                        <div className="mt-2">
                          <ol className="text-xs text-blue-gray-600 space-y-0.5 list-decimal pl-4 mb-1.5">
                            {step.steps.map((s, i) => (
                              <li key={i}>{s}</li>
                            ))}
                          </ol>
                          <a
                            href={step.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                          >
                            {step.linkLabel}
                            <ExternalLink size={11} />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center gap-3 pt-3 border-t border-amber-100">
            <button
              onClick={goToApiKeys}
              className="inline-flex items-center gap-2 text-sm font-semibold bg-accent hover:bg-accent/90 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Key size={14} />
              Gérer mes clés API
            </button>
            <span className="text-xs text-blue-gray-400">
              Profil → onglet « Clés API »
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
